import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { sectionsApi, kitchenApi, tablesApi, checksApi } from '../services/api';
import { useOrderStore } from '../stores/orderStore';
import { useToast } from '../components/Toast';
import { Users, Clock, DollarSign, RefreshCw, Grid3X3, LayoutGrid, ZoomIn, ZoomOut, ChefHat, AlertTriangle, X, ShoppingCart, Trash2, Sparkles, ArrowRightLeft } from 'lucide-react';
import clsx from 'clsx';

interface Table {
  id: string;
  number: string;
  name?: string;
  capacity: number;
  shape: string;
  positionX: number;
  positionY: number;
  status: string;
  currentCheck?: {
    id: string;
    total: number;
    partySize: number;
    serverName: string;
    openedAt: string;
    guestAllergens?: string[];
    currentCourse?: number;
    totalCourses?: number;
  };
  dwellMinutes?: number;
}

interface Section {
  id: string;
  name: string;
  color: string;
  tables: Table[];
}

const TABLE_SIZE = 60;
const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 500;

export default function TablesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const { setTable, setCheckId, setPartySize } = useOrderStore();
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tableActionModal, setTableActionModal] = useState<Table | null>(null);
  const [moveTableModal, setMoveTableModal] = useState<Table | null>(null);

  // Detect if mobile and set default view
  const [viewMode, setViewMode] = useState<'floor' | 'grid'>(() => {
    return window.innerWidth < 768 ? 'grid' : 'floor';
  });

  // Calculate scale based on container width
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current && viewMode === 'floor') {
        const containerWidth = containerRef.current.clientWidth - 32; // padding
        const newScale = Math.min(1, containerWidth / CANVAS_WIDTH);
        setScale(Math.max(0.4, newScale)); // minimum 40% scale
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [viewMode]);

  const { data: sections, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return (response.data || []) as Section[];
    },
    refetchInterval: 15000,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Fetch kitchen tickets to show status on tables
  const { data: kitchenTickets } = useQuery({
    queryKey: ['kitchen-tickets'],
    queryFn: async () => {
      const response = await kitchenApi.getTickets();
      return response.data || [];
    },
    refetchInterval: 10000,
    staleTime: 5000,
  });

  // Map kitchen status by table number
  const kitchenStatusByTable = useMemo(() => {
    const statusMap: Record<string, { status: string; itemCount: number; readyCount: number }> = {};

    if (kitchenTickets) {
      kitchenTickets.forEach((ticket: any) => {
        const tableNum = ticket.tableNumber || ticket.tableName;
        if (!tableNum) return;

        const items = ticket.items || [];
        // Kitchen API returns kitchenStatus values: 'queued', 'cooking', 'ready', 'bumped'
        const pendingItems = items.filter((i: any) =>
          i.status === 'pending' || i.status === 'queued'
        ).length;
        const inProgressItems = items.filter((i: any) =>
          i.status === 'in_progress' || i.status === 'cooking'
        ).length;
        const completedItems = items.filter((i: any) =>
          i.status === 'completed' || i.status === 'ready' || i.status === 'bumped'
        ).length;
        const totalItems = items.length;

        // Determine overall status for display
        let status = 'pending';
        if (completedItems === totalItems && totalItems > 0) {
          status = 'ready';
        } else if (inProgressItems > 0) {
          status = 'cooking';
        }

        statusMap[tableNum] = {
          status,
          itemCount: totalItems - completedItems,
          readyCount: completedItems,
        };
      });
    }

    return statusMap;
  }, [kitchenTickets]);

  // Clear table mutation - comprehensive invalidation for cross-view sync
  const clearTableMutation = useMutation({
    mutationFn: async (table: Table) => {
      // Close check first if exists
      if (table.currentCheck?.id) {
        try {
          await checksApi.close(table.currentCheck.id);
        } catch (e) {
          console.log('Check already closed or not found');
        }
      }
      // Then clear the table
      const response = await tablesApi.clear(table.id);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to clear table');
      }
      return response;
    },
    onSuccess: () => {
      // Comprehensive invalidation for cross-view consistency
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['checks'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      setTableActionModal(null);
    },
    onError: (error: any) => {
      console.error('Failed to clear table:', error);
      showError(error?.message || 'Failed to clear table. Please try again.');
    },
  });

  // Mark table as clean mutation
  const markCleanMutation = useMutation({
    mutationFn: async (tableId: string) => {
      return tablesApi.updateStatus(tableId, 'available');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      setTableActionModal(null);
    },
    onError: (error) => {
      console.error('Failed to mark table clean:', error);
      alert('Failed to update table. Please try again.');
    },
  });

  // Move table mutation - comprehensive invalidation
  const moveTableMutation = useMutation({
    mutationFn: async ({ checkId, toTableId }: { checkId: string; toTableId: string }) => {
      return checksApi.transfer(checkId, { toTableId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['checks'] });
      setMoveTableModal(null);
      setTableActionModal(null);
    },
    onError: (error) => {
      console.error('Failed to move table:', error);
      alert('Failed to move table. Please try again.');
    },
  });

  // Get available tables for move modal
  const availableTables = useMemo(() => {
    if (!sections || !moveTableModal) return [];
    return sections.flatMap((s: Section) => s.tables)
      .filter((t: Table) => t.id !== moveTableModal.id && (t.status === 'available' || t.status === 'reserved'));
  }, [sections, moveTableModal]);

  const handleMoveTable = (toTable: Table) => {
    if (!moveTableModal?.currentCheck?.id) return;
    moveTableMutation.mutate({
      checkId: moveTableModal.currentCheck.id,
      toTableId: toTable.id,
    });
  };

  const handleTableClick = async (table: Table) => {
    // For available tables, go directly to order
    if (table.status === 'available') {
      setTable(table.id, table.number);
      setCheckId(null);
      setPartySize(1);
      navigate(`/order/${table.id}`);
      return;
    }

    // For all other statuses (occupied, seated, needs_cleaning, etc), show action modal
    setTableActionModal(table);
  };

  const handleMarkClean = (table: Table) => {
    markCleanMutation.mutate(table.id);
  };

  const handleOpenOrder = (table: Table) => {
    setTable(table.id, table.number);
    if (table.currentCheck) {
      setCheckId(table.currentCheck.id);
      setPartySize(table.currentCheck.partySize);
    } else {
      setCheckId(null);
      setPartySize(1);
    }
    setTableActionModal(null);
    navigate(`/order/${table.id}`);
  };

  const handleClearTable = (table: Table) => {
    if (window.confirm(`Clear Table ${table.number}? This will close the check and free the table.`)) {
      clearTableMutation.mutate(table);
    }
  };

  const formatDwellTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  // Get all tables or filter by section
  const displaySections = selectedSection
    ? sections?.filter(s => s.id === selectedSection) || []
    : sections || [];

  const allTables = displaySections.flatMap(s =>
    s.tables.map(t => ({ ...t, sectionColor: s.color, sectionName: s.name }))
  );

  const availableCount = allTables.filter(t => t.status === 'available').length;
  const occupiedCount = allTables.filter(t => t.status !== 'available').length;

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Compact Header */}
      <div className="bg-white border-b border-slate-200">
        {/* Top Row - Title and Actions */}
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-base sm:text-lg">Tables</h2>
            <div className="flex items-center gap-1 text-xs">
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                {availableCount} free
              </span>
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                {occupiedCount} busy
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* View Toggle */}
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('floor')}
                className={clsx(
                  'p-1.5 rounded transition-colors',
                  viewMode === 'floor' ? 'bg-white shadow-sm text-primary-600' : 'text-slate-400'
                )}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={clsx(
                  'p-1.5 rounded transition-colors',
                  viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-slate-400'
                )}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => refetch()}
              disabled={isRefetching}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
            >
              <RefreshCw className={clsx('w-4 h-4', isRefetching && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Section Filter - Scrollable */}
        {sections && sections.length > 1 && (
          <div className="px-3 pb-2 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 min-w-max">
              <button
                onClick={() => setSelectedSection(null)}
                className={clsx(
                  'px-2.5 py-1 text-xs font-medium rounded-full transition-colors whitespace-nowrap',
                  !selectedSection
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600 active:bg-slate-200'
                )}
              >
                All
              </button>
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setSelectedSection(section.id)}
                  className={clsx(
                    'px-2.5 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1.5 whitespace-nowrap',
                    selectedSection === section.id
                      ? 'text-white'
                      : 'bg-slate-100 text-slate-600 active:bg-slate-200'
                  )}
                  style={{
                    backgroundColor: selectedSection === section.id ? section.color : undefined,
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedSection !== section.id ? section.color : 'white' }}
                  />
                  {section.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div ref={containerRef} className="flex-1 overflow-auto">
        {viewMode === 'floor' ? (
          /* Floor Plan View - Scaled for mobile */
          <div className="p-4">
            {/* Zoom Controls for Mobile */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-slate-500">
                Pinch or use buttons to zoom
              </div>
              <div className="flex items-center gap-1 bg-white rounded-lg shadow-sm border border-slate-200">
                <button
                  onClick={() => setScale(s => Math.max(0.4, s - 0.1))}
                  className="p-1.5 text-slate-500 hover:text-slate-700"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium w-10 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale(s => Math.min(1.2, s + 0.1))}
                  className="p-1.5 text-slate-500 hover:text-slate-700"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scaled Canvas — the outer box is sized to the SCALED dimensions.
                `transform: scale()` only shrinks visually; the layout box stays
                CANVAS_WIDTH×CANVAS_HEIGHT, so without this the overflow container
                would still pan/swipe across the full 800px. */}
            <div
              className="bg-white rounded-xl border border-slate-200 shadow-sm mx-auto overflow-hidden"
              style={{ width: CANVAS_WIDTH * scale, height: CANVAS_HEIGHT * scale }}
            >
              <div
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  width: CANVAS_WIDTH,
                  height: CANVAS_HEIGHT,
                }}
              >
                <div
                  className="relative w-full h-full"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, #f1f5f9 1px, transparent 1px),
                      linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)
                    `,
                    backgroundSize: '20px 20px',
                  }}
                >
                  {allTables.map((table: any) => {
                    const isOccupied = table.status !== 'available';
                    const tableWidth = table.shape === 'rectangle' ? TABLE_SIZE * 1.4 : TABLE_SIZE;
                    const kitchenStatus = kitchenStatusByTable[table.number];

                    return (
                      <button
                        key={table.id}
                        onClick={() => handleTableClick(table)}
                        className={clsx(
                          'absolute flex flex-col items-center justify-center transition-transform',
                          'active:scale-90',
                          table.shape === 'round' ? 'rounded-full' : 'rounded-xl'
                        )}
                        style={{
                          left: table.positionX,
                          top: table.positionY,
                          width: tableWidth,
                          height: TABLE_SIZE,
                          backgroundColor: isOccupied ? table.sectionColor : `${table.sectionColor}25`,
                          borderWidth: 3,
                          borderColor: table.sectionColor,
                          borderStyle: 'solid',
                        }}
                      >
                        {/* Allergen Alert Badge */}
                        {table.currentCheck?.guestAllergens && table.currentCheck.guestAllergens.length > 0 && (
                          <div
                            className="absolute -top-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center bg-red-600 text-white shadow-sm animate-pulse"
                            title={`Allergies: ${table.currentCheck.guestAllergens.join(', ')}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                          </div>
                        )}
                        {/* Kitchen Status Badge - matches KDS terminology */}
                        {kitchenStatus && (
                          <div
                            className={clsx(
                              'absolute -top-1 -right-1 px-1 min-w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shadow-sm',
                              kitchenStatus.status === 'ready' && 'bg-emerald-500 animate-pulse',
                              kitchenStatus.status === 'cooking' && 'bg-amber-500',
                              kitchenStatus.status === 'pending' && 'bg-red-500'
                            )}
                            title={`Kitchen: ${kitchenStatus.status === 'ready' ? 'Ready to serve' : kitchenStatus.status === 'cooking' ? 'Cooking' : 'Needs to fire'}`}
                          >
                            {kitchenStatus.status === 'ready' ? 'RDY' : kitchenStatus.status === 'cooking' ? 'COOK' : 'FIRE'}
                          </div>
                        )}
                        <span className={clsx(
                          'font-bold text-sm',
                          isOccupied ? 'text-white' : 'text-slate-700'
                        )}>
                          T{table.number}
                        </span>
                        <span className={clsx(
                          'text-[10px] flex items-center gap-0.5',
                          isOccupied ? 'text-white/80' : 'text-slate-500'
                        )}>
                          <Users className="w-2.5 h-2.5" />
                          {table.currentCheck?.partySize || table.capacity}
                        </span>
                        {table.currentCheck && (
                          <span className="text-[10px] text-white/90 font-medium">
                            {formatCurrency(table.currentCheck.total)}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {allTables.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <Grid3X3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No tables configured</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Legend - Compact for mobile */}
            <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
              {/* Section Legend */}
              {displaySections.map(section => (
                <div key={section.id} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: section.color }}
                  />
                  <span>{section.name}</span>
                  <span className="text-slate-400">
                    {section.tables.filter(t => t.status === 'available').length}/{section.tables.length}
                  </span>
                </div>
              ))}
              {/* Kitchen Status Legend */}
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-300">
                <span className="text-xs text-slate-500">Kitchen:</span>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-[10px] text-slate-600">FIRE</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-[10px] text-slate-600">COOK</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-[10px] text-slate-600">RDY</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Grid View - Optimized for mobile */
          <div className="p-3 space-y-4">
            {displaySections.map(section => (
              <div key={section.id}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: section.color }}
                  />
                  <h3 className="font-medium text-sm text-slate-700">{section.name}</h3>
                  <span className="text-xs text-slate-400">
                    {section.tables.filter(t => t.status === 'available').length} free
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {section.tables.map(table => {
                    const isOccupied = table.status !== 'available';
                    const kitchenStatus = kitchenStatusByTable[table.number];

                    return (
                      <button
                        key={table.id}
                        onClick={() => handleTableClick(table)}
                        className={clsx(
                          'p-3 rounded-xl transition-all text-left active:scale-95 relative',
                          isOccupied
                            ? 'shadow-sm'
                            : 'bg-white border border-slate-200 shadow-sm'
                        )}
                        style={{
                          backgroundColor: isOccupied ? `${section.color}15` : undefined,
                          borderColor: isOccupied ? section.color : undefined,
                          borderWidth: isOccupied ? 2 : undefined,
                        }}
                      >
                        {/* Allergen Alert Badge */}
                        {table.currentCheck?.guestAllergens && table.currentCheck.guestAllergens.length > 0 && (
                          <div
                            className="absolute -top-1.5 -left-1.5 px-1.5 py-0.5 rounded-full flex items-center gap-1 text-[10px] font-bold bg-red-600 text-white shadow-sm animate-pulse"
                            title={`Allergies: ${table.currentCheck.guestAllergens.join(', ')}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            !
                          </div>
                        )}
                        {/* Kitchen Status Badge - matches KDS terminology */}
                        {kitchenStatus && (
                          <div
                            className={clsx(
                              'absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full flex items-center gap-1 text-[10px] font-bold text-white shadow-sm',
                              kitchenStatus.status === 'ready' && 'bg-emerald-500 animate-pulse',
                              kitchenStatus.status === 'cooking' && 'bg-amber-500',
                              kitchenStatus.status === 'pending' && 'bg-red-500'
                            )}
                          >
                            <ChefHat className="w-3 h-3" />
                            {kitchenStatus.status === 'ready' ? 'RDY' : kitchenStatus.status === 'cooking' ? 'COOK' : 'FIRE'}
                          </div>
                        )}
                        {/* Table Header */}
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-base">
                            T{table.number}
                          </span>
                          <span className="text-xs text-slate-400 flex items-center gap-0.5">
                            <Users className="w-3 h-3" />
                            {table.currentCheck?.partySize || table.capacity}
                          </span>
                        </div>

                        {/* Status */}
                        {!isOccupied ? (
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span className="text-xs text-emerald-600 font-medium">Available</span>
                          </div>
                        ) : table.currentCheck ? (
                          <div className="space-y-0.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-1 text-slate-500">
                                <Clock className="w-3 h-3" />
                                {formatDwellTime(table.dwellMinutes || 0)}
                              </span>
                              <span className="font-semibold text-slate-900">
                                {formatCurrency(table.currentCheck.total)}
                              </span>
                            </div>
                            {/* Course Progress */}
                            {table.currentCheck.totalCourses && table.currentCheck.totalCourses > 0 ? (
                              <div className="flex items-center gap-1">
                                <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-amber-500 rounded-full transition-all"
                                    style={{
                                      width: `${((table.currentCheck.currentCourse || 0) / table.currentCheck.totalCourses) * 100}%`
                                    }}
                                  />
                                </div>
                                <span className="text-[10px] text-amber-600 font-medium">
                                  C{table.currentCheck.currentCourse || 0}/{table.currentCheck.totalCourses}
                                </span>
                              </div>
                            ) : (
                              <p className="text-[10px] text-slate-400 truncate">
                                {table.currentCheck.serverName}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <span className="text-xs text-amber-600 font-medium">Occupied</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {allTables.length === 0 && (
              <div className="text-center text-slate-400 py-12">
                <Grid3X3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No tables found</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table Action Modal */}
      {tableActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
            {/* Header */}
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Table {tableActionModal.number}</h3>
                {tableActionModal.status === 'needs_cleaning' ? (
                  <p className="text-sm text-amber-600">Needs cleaning</p>
                ) : tableActionModal.currentCheck ? (
                  <div>
                    <p className="text-sm text-slate-500">
                      {tableActionModal.currentCheck.partySize} guests · {formatCurrency(tableActionModal.currentCheck.total)}
                    </p>
                    {tableActionModal.currentCheck.totalCourses && tableActionModal.currentCheck.totalCourses > 0 && (
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{
                              width: `${((tableActionModal.currentCheck.currentCourse || 0) / tableActionModal.currentCheck.totalCourses) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-xs text-amber-600 font-medium whitespace-nowrap">
                          Course {tableActionModal.currentCheck.currentCourse || 0} of {tableActionModal.currentCheck.totalCourses}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">{tableActionModal.status}</p>
                )}
              </div>
              <button
                onClick={() => setTableActionModal(null)}
                className="p-2 rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Actions - different based on table status */}
            <div className="p-4 space-y-3">
              {tableActionModal.status === 'needs_cleaning' ? (
                <>
                  {/* Needs Cleaning Actions */}
                  <button
                    onClick={() => handleMarkClean(tableActionModal)}
                    disabled={markCleanMutation.isPending}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-semibold">
                        {markCleanMutation.isPending ? 'Marking...' : 'Mark Clean'}
                      </p>
                      <p className="text-sm text-emerald-600/70">Table is ready for new guests</p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleOpenOrder(tableActionModal)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                  >
                    <Users className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-semibold">Seat New Guests</p>
                      <p className="text-sm text-primary-600/70">Start a new check at this table</p>
                    </div>
                  </button>
                </>
              ) : (
                <>
                  {/* Occupied Table Actions */}
                  <button
                    onClick={() => handleOpenOrder(tableActionModal)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                  >
                    <ShoppingCart className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-semibold">Open Order</p>
                      <p className="text-sm text-primary-600/70">View or add items to this check</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setMoveTableModal(tableActionModal);
                      setTableActionModal(null);
                    }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    <ArrowRightLeft className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-semibold">Move Table</p>
                      <p className="text-sm text-amber-600/70">Move party to a different table</p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleClearTable(tableActionModal)}
                    disabled={clearTableMutation.isPending}
                    className="w-full flex items-center gap-3 p-4 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-semibold">
                        {clearTableMutation.isPending ? 'Clearing...' : 'Clear Table'}
                      </p>
                      <p className="text-sm text-red-600/70">Close check and free the table</p>
                    </div>
                  </button>
                </>
              )}
            </div>

            {/* Cancel */}
            <div className="p-4 pt-0">
              <button
                onClick={() => setTableActionModal(null)}
                className="w-full p-3 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Table Modal */}
      {moveTableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Move Table {moveTableModal.number}</h3>
                <p className="text-sm text-slate-500">
                  {moveTableModal.currentCheck?.partySize} guests · Select destination table
                </p>
              </div>
              <button
                onClick={() => setMoveTableModal(null)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Available Tables List */}
            <div className="flex-1 overflow-y-auto p-4 scroll-bounce">
              {availableTables.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No available tables to move to.</p>
                  <p className="text-sm mt-1">All tables are currently occupied.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {availableTables.map((table: Table) => (
                    <button
                      key={table.id}
                      onClick={() => handleMoveTable(table)}
                      disabled={moveTableMutation.isPending}
                      className="p-4 rounded-xl border-2 border-slate-200 hover:border-primary-500 hover:bg-primary-50 transition-colors text-left disabled:opacity-50"
                    >
                      <p className="font-bold text-lg">Table {table.number}</p>
                      <p className="text-sm text-slate-500">
                        Capacity: {table.capacity} · {table.status === 'reserved' ? 'Reserved' : 'Available'}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t">
              <button
                onClick={() => setMoveTableModal(null)}
                disabled={moveTableMutation.isPending}
                className="w-full p-3 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors font-medium"
              >
                {moveTableMutation.isPending ? 'Moving...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
